import * as React from "react";
import { Backdrop, Button, IconButton, Modal } from "@mui/material";
import { makeStyles } from "@mui/styles";
import tokens from "../../styles/designTokens";
import { title2 } from "../../styles/typography";
import CloseIcon from "../../assets/svgs/Close.svg";
import { useEffect, useRef } from "react";

const useStyles = makeStyles(() => ({
  modalStyle: {
    height: 'fit-content',
    color: tokens.core1,
    backgroundColor: tokens.white,
    borderRadius: '0.25rem',
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
    left: '0.5rem',
    right: '0.5rem',
    padding: '1.5rem',
    '& .MuiBackdrop-root.MuiModal-backdrop': {
      backgroundColor: 'transparent'
    },
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  titleStyle: {
    ...title2,
    height: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    marginTop: '1.5rem',
    marginBottom: '2.5rem',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  modalButton: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '0.4rem',
    marginLeft: '1rem',
    height: '2.25rem',
  },
}));

type ConfirmationModalProps = {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  buttonAction: () => void;
  cancelAction: () => void;
  disabled?: boolean;
};

const ConfirmationModal = ({
  title,
  children,
  isOpen,
  onClose,
  buttonAction,
  cancelAction,
  disabled = false,
}: ConfirmationModalProps) => {
  const classes = useStyles();
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        cancelClearCommentsRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  const cancelClearCommentsRef = useRef<HTMLButtonElement>(null);
  const closeModalButtonRef = useRef<HTMLButtonElement>(null);
  const clearCommentsButtonRef = useRef<HTMLButtonElement>(null);
  const clearCommentsKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      if (event.shiftKey) {
        cancelClearCommentsRef.current?.focus();
      } else {
        closeModalButtonRef.current?.focus();
      }
    }
  };

  const closeModalOnKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      if (event.shiftKey) {
        clearCommentsButtonRef.current?.focus();
      } else {
        cancelClearCommentsRef.current?.focus();
      }
    }
  };

  return (
    <Backdrop open={isOpen} onClick={() => { }}>
      <Modal
        open={isOpen}
        onClose={onClose}
        className={classes.modalStyle}
        aria-modal="true"
        role="dialog"
        aria-label="Refresh analysis confirmation modal"
      >
        <>
          <div className={classes.modalHeader}>
            <div className={classes.titleStyle}>{title}</div>
            <IconButton aria-label="Cancel" size="large" onClick={cancelAction} ref={closeModalButtonRef} onKeyDown={closeModalOnKeyDown}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.content}>
            {children}
          </div>
          <div className={classes.buttonContainer}>
            <Button ref={cancelClearCommentsRef} color="primary" variant="outlined" className={`${classes.modalButton}`} onClick={cancelAction}>
              Cancel
            </Button>
            <Button disabled={disabled} color="error" variant="contained" className={`${classes.modalButton}`} onClick={buttonAction} onKeyDown={clearCommentsKeyDown} ref={clearCommentsButtonRef}>
              Refresh analysis
            </Button>
          </div>
        </>
      </Modal>
    </Backdrop>
  );
};

export default ConfirmationModal;
